import pick from 'lodash/pick';
import { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';

import StripeElements from 'components/common/forms/StripeElements';
import Heading from 'components/common/Heading';
import ToastMessage from 'components/common/ToastMessage';
import CheckoutForm from 'components/signUpFunnel/checkout/CheckoutForm';
import {
  UserContext,
  VoucherContext,
  ProgressBarContext,
} from 'components/signUpFunnel/Contexts.js';
import * as Routes from 'helpers/routes';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import { TEMPO } from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import { availableZipCode, getBox } from 'services/RequestService/endpoints/signUp';

const BillingContainer = () => {
  const { user, setUser } = useContext(UserContext);
  const { voucher, setVoucher } = useContext(VoucherContext);

  const [zipCodeDeliverability, setZipCodeDeliverability] = useState({
    message: '',
    isDeliverable: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [box, setBox] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const navigate = useNavigate();
  const { setProgress } = useContext(ProgressBarContext);

  useAnalyticsEffect(({ events, logEvent }) => {
    logEvent(events.pickMeals[TEMPO].billing.viewed, { mealPlanSlug: TEMPO });
  }, []);

  useEffect(() => {
    setProgress({ percent: '70' });
  }, [setProgress]);

  useEffect(() => {
    const { servings, weeklyMeals, signupZip } = pick(user, [
      'servings',
      'weeklyMeals',
      'signupZip',
    ]);

    const fetchBox = async () => {
      try {
        const response = await getBox({
          params: {
            servings,
            weeklyMeals,
            zipCode: signupZip,
            estimated: false,
          },
        });
        setDisableSubmit(false);
        if (response.ok) {
          setBox(prevBox => ({ ...prevBox, ...response.box }));
        } else {
          navigate(Routes.PICK_MEALS_MENU_PATH, { replace: true });
        }
      } catch (error) {
        setDisableSubmit(false);
        ErrorLogging.captureException(error.message);
      }
    };

    if (!user?.completedSignup && servings && weeklyMeals && signupZip) {
      setDisableSubmit(true);
      fetchBox();
    }
  }, [user, voucher]);

  const handleZipCodeChange = zipCode => {
    setDisableSubmit(true);
    availableZipCode({ params: { zip: zipCode, mealPlanSlug: user.mealPlanSlug } }).then(
      response => {
        setDisableSubmit(false);
        if (response.ok) {
          setUser({
            ...user,
            signupZip: zipCode,
            signupCity: response?.zipCodes?.city,
            signupState: response?.zipCodes?.state,
          });
          setZipCodeDeliverability({
            message: response?.zipCodes?.message,
            isDeliverable: true,
          });
        } else {
          setZipCodeDeliverability({
            message: response?.error,
            isDeliverable: false,
          });
        }
      }
    );
  };

  return (
    <div className="flex grow flex-col overflow-x-hidden">
      <Heading
        brandStylesOverrides="h2"
        className="hidden bg-tempo-gray-40 pt-6 text-center bp2:pt-12 bp3:block bp3:pb-12"
      >
        Billing Information
      </Heading>
      <div className="grow bg-tempo-gray-40">
        <div className="mx-auto w-full max-w-1100">
          <StripeElements variant="refreshed">
            <CheckoutForm
              user={user}
              setUser={setUser}
              handleZipCodeChange={handleZipCodeChange}
              zipCodeDeliverability={zipCodeDeliverability}
              isSubmitting={isSubmitting}
              setIsSubmitting={setIsSubmitting}
              disableSubmit={disableSubmit}
              setDisableSubmit={setDisableSubmit}
              disabled={zipCodeDeliverability.isDeliverable === false}
              mobileSignUp={false}
              box={box}
              setBox={setBox}
              setToastMessage={setToastMessage}
              voucher={voucher}
              setVoucher={setVoucher}
            />
          </StripeElements>
        </div>
      </div>
      <ToastMessage
        content={toastMessage?.message}
        visible={Boolean(toastMessage)}
        type={toastMessage?.type}
      />
    </div>
  );
};

export default BillingContainer;
